import React, { useRef, useState } from "react";
import CommonModal from "../Ui-Kits/Modal/Common/CommonModal";
import { useEmployeeProfile } from "../../hooks/useEmployeeProfile";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import {
  setEmployeeProfileImage,
} from "../../features/employeeManagement/redux/slice/EmployeeManagementSlice";
import { Btn, Image, P } from "../../AbstractElements";
import IconComponent from "../../CommonElements/IconComponent";
import { getProfileImage } from "../../features/employeeManagement/utils/format";
import { openModal } from "../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import styled from "styled-components";
import {
  PROFILE_IMAGE_ACCEPT,
} from "../../features/employeeManagement/components/modals/ProfileImageCropModal/profileImageCrop.constants";
import { validateProfileImageFile } from "../../features/employeeManagement/components/modals/ProfileImageCropModal/profileImageCrop.utils";
import { alertManager } from "../../utils/AlertManager";

const ModalShell = styled.div`
  padding: 6px 4px 2px;
  text-align: center;

  @media (max-width: 575px) {
    padding: 2px 0;
  }
`;

const Title = styled.h5`
  margin: 0;
  color: #0f172a;
  font-size: 17px;
  font-weight: 700;
`;

const Subtitle = styled(P)`
  max-width: 360px;
  margin: 6px auto 16px !important;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;

  @media (max-width: 575px) {
    max-width: 280px;
    margin-bottom: 12px !important;
  }
`;

const PhotoCard = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);

  @media (max-width: 575px) {
    width: 100%;
    max-width: 240px;
    padding: 12px 14px;
  }
`;

const PhotoFrame = styled.button`
  position: relative;
  width: clamp(112px, 36vw, 136px);
  height: clamp(112px, 36vw, 136px);
  border: 0;
  border-radius: 999px;
  padding: 5px;
  background: #ffffff;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  cursor: pointer;
`;

const ProfileImage = styled(Image)`
  width: 100%;
  height: 100%;
  max-height: 100%;
  object-fit: cover;
`;

const CameraButton = styled.span`
  position: absolute;
  right: 6px;
  bottom: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 2px solid #ffffff;
  border-radius: 999px;
  color: #ffffff;
  background: #2563eb;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.32);

  @media (max-width: 575px) {
    width: 30px;
    height: 30px;
  }
`;

const HintText = styled(P)`
  max-width: 330px;
  margin: 14px auto 0 !important;
  color: #64748b;
  font-size: 11px;
  line-height: 1.45;
`;

const ValidationMessage = styled.p`
  margin: 12px 0 0;
  color: #dc2626;
  font-size: 12px;
  font-weight: 600;
`;

const AlertProfilePicModal = () => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [validationError, setValidationError] = useState("");

  const { employeeProfileDetails } = useEmployeeProfile();
  const employeeProfileUrl = employeeProfileDetails?.jobInformation?.profile;

  const { isOpenAlertPicModal } = useAppSelector(
    (state) => state.employeeManagement
  );

  const handleToggle = () => {};

  const handleOpenFilePicker = () => fileInputRef.current?.click();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validationMessage = validateProfileImageFile(file);
      if (validationMessage) {
        setValidationError(validationMessage);
        alertManager.showAlert({
          type: "warning",
          title: `${employeeProfileDetails?.jobInformation?.name ?? "Profile"} Info`,
          message: validationMessage,
          source: "profileImageCropModal",
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      setValidationError("");
      const imageUrl = URL.createObjectURL(file);
      dispatch(setEmployeeProfileImage(imageUrl));
      dispatch(openModal({ modalType: "profileImageCropModal" }));
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <CommonModal
      centered
      size="md"
      backdrop="static"
      showFooter={false}
      toggle={handleToggle}
      isOpen={isOpenAlertPicModal}
      modalBodyClassName="px-3 py-3"
    >
      <ModalShell>
        <Title>Upload Profile Photo</Title>
        <Subtitle>
          Upload your profile photo to continue using the app.
        </Subtitle>

        <input
          className="d-none"
          type="file"
          accept={PROFILE_IMAGE_ACCEPT}
          ref={fileInputRef}
          aria-label="Choose JPG or PNG profile photo"
          onChange={handleFileChange}
        />

        <PhotoCard>
          <PhotoFrame
            aria-label="Choose profile photo"
            type="button"
            onClick={handleOpenFilePicker}
          >
            <ProfileImage
              className="img-fluid rounded-circle"
              src={getProfileImage(
                employeeProfileUrl || "",
                employeeProfileDetails?.employeePersonalInformation?.gender ||
                  "male"
              )}
              alt="user-img"
            />
            <CameraButton>
              <IconComponent className="fa fa-camera" />
            </CameraButton>
          </PhotoFrame>
          <P className="f-12 mb-0 text-muted">
            JPG or PNG, below 2 MB.
          </P>
          {validationError && (
            <ValidationMessage aria-live="polite">
              {validationError}
            </ValidationMessage>
          )}
        </PhotoCard>

        <div className="d-flex justify-content-center mt-4">
          {/* <Btn className="btn-secondary btn-small" onClick={handleToggle}>
            Remind Me Later
          </Btn> */}
          <Btn
            className="btn-primary btn-small px-4"
            onClick={handleOpenFilePicker}
            type="button"
          >
            Upload
          </Btn>
        </div>

        <HintText>
          Access will unlock after your profile photo is updated.
        </HintText>
      </ModalShell>
    </CommonModal>
  );
};

export default AlertProfilePicModal;
